import Backdrop from '@material-ui/core/Backdrop';
import Portal from '@material-ui/core/Portal';
import LoadingBM from 'components-v2/atoms/LoadingBM';
import { useTranslation } from 'next-i18next';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import NotifyUtils from 'utils/NotifyUtils';

// actions ổn định (không đổi khi đóng/mở) tách khỏi isOpen: card chỉ cần mở popup thì không
// re-render mỗi lần popup đóng/mở
const VoucherDetailPopupActionsContext = createContext(null);
const VoucherDetailPopupStateContext = createContext(false);
// nội bộ: báo provider khi tải chunk dialog lỗi
const DialogLoadErrorContext = createContext(null);
// KH: tuỳ chọn của lần mở hiện tại mà nội dung popup cần (vd onCopy để tracking
// CLICK_COPY_VOUCHER_ON_CART_PAGE khi mở từ modal voucher của giỏ)
const EMPTY_POPUP_OPTIONS = { onCopy: null };
const VoucherDetailPopupOptionsContext = createContext(EMPTY_POPUP_OPTIONS);

const NOOP_ACTIONS = {
  openVoucherDetail: () => {},
  closeVoucherDetail: () => {},
};

// trên Modal / Dialog của MUI (1300): phủ cả modal giỏ đang mở
const DIALOG_LOADING_Z_INDEX = 1301;

// Đang tải chunk dialog: phủ màn hình + spinner để trang phía sau (vd nút thanh toán khi sheet giỏ mobile
// đã ẩn) không bấm được (R-CART S-1). Chỉ hiện khi popup đang mở (đổi trang / đóng giữa chừng thì thôi);
// bấm lớp phủ = huỷ mở popup.
const DialogLoadingOverlay = () => {
  const isOpen = useContext(VoucherDetailPopupStateContext);
  const { closeVoucherDetail } = useContext(VoucherDetailPopupActionsContext) || NOOP_ACTIONS;
  if (!isOpen) return null;
  return (
    <Portal>
      <Backdrop open style={{ zIndex: DIALOG_LOADING_Z_INDEX }} onClick={closeVoucherDetail}>
        <LoadingBM />
      </Backdrop>
    </Portal>
  );
};

// Tải chunk lỗi (mất mạng, hash chunk cũ sau deploy): provider đóng popup (isOpen về false, gọi onClose)
// và giữ `retry` cho lần mở sau; toast lỗi nếu lúc đó popup đang mở.
const DialogLoadError = ({ retry }) => {
  const onLoadError = useContext(DialogLoadErrorContext);
  const { t, ready } = useTranslation('voucherDetail');
  const [shouldNotify, setShouldNotify] = useState(false);

  useEffect(() => {
    setShouldNotify(!!onLoadError?.(retry));
  }, []);

  useEffect(() => {
    if (!shouldNotify || !ready) return;
    setShouldNotify(false);
    NotifyUtils.error(t('voucherDetail:toast.error'));
  }, [shouldNotify, ready]);

  return null;
};

const DialogLoading = ({ error, retry }) =>
  error ? <DialogLoadError retry={retry} /> : <DialogLoadingOverlay />;

// Provider được mount ở _app: dialog kéo theo ProductCardNew, ProductServiceV2... nên tách chunk,
// chỉ tải khi mở popup lần đầu (hoặc khi preloadVoucherDetail)
const VoucherDetailDialog = dynamic(() => import('./VoucherDetailDialog'), {
  ssr: false,
  loading: DialogLoading,
});

// Tải trước chunk dialog (vd lúc mở modal voucher giỏ) để lần bấm "Điều kiện" đầu tiên mở ngay.
// Không bao giờ reject. Dùng import() riêng (webpack dùng chung chunk) chứ không preload của
// next/dynamic: preload lỗi sẽ kẹt trạng thái lỗi của dynamic tới lần retry.
export const preloadVoucherDetail = () =>
  import('./VoucherDetailDialog').then(
    () => undefined,
    () => undefined,
  );

const runCallbacks = (callbacks) =>
  callbacks.forEach((callback) => {
    try {
      callback();
    } catch (error) {
      console.error(error);
    }
  });

const VoucherDetailPopupRoot = ({ children, isMobile }) => {
  const router = useRouter();
  const [popup, setPopup] = useState({
    open: false,
    code: '',
    initialVoucher: null,
    onCopy: null,
  });
  const [hasOpened, setHasOpened] = useState(false);
  const isOpenRef = useRef(false);
  // giống handleOpen(code, cb, onCloseCb) của VN: gom các onClose, gọi hết khi popup đóng
  const onCloseCallbacksRef = useRef([]);
  // retry của next/dynamic sau khi tải chunk lỗi; gọi ở lần mở kế tiếp
  const retryLoadRef = useRef(null);

  // initialVoucher (không bắt buộc): dữ liệu card đang có, hiển thị ngay trong lúc gọi API chi tiết
  // options.onClose (không bắt buộc): gọi khi popup đóng (bấm đóng, đổi trang...), vd refetch list
  // options.onCopy (không bắt buộc, KH): gọi khi khách copy mã trong popup, vd tracking của giỏ
  const openVoucherDetail = useCallback((code, initialVoucher = null, options = {}) => {
    if (!code) return;
    const onClose = options?.onClose;
    if (typeof onClose === 'function') onCloseCallbacksRef.current.push(onClose);
    const onCopy = typeof options?.onCopy === 'function' ? options.onCopy : null;
    isOpenRef.current = true;
    setHasOpened(true);
    setPopup({ open: true, code, initialVoucher: initialVoucher || null, onCopy });
    const retryLoad = retryLoadRef.current;
    if (retryLoad) {
      retryLoadRef.current = null;
      retryLoad();
    }
  }, []);

  const closeVoucherDetail = useCallback(() => {
    if (!isOpenRef.current) return;
    isOpenRef.current = false;
    setPopup((prev) => (prev.open ? { ...prev, open: false } : prev));
    const callbacks = onCloseCallbacksRef.current;
    onCloseCallbacksRef.current = [];
    runCallbacks(callbacks);
  }, []);

  // → true nếu popup đang mở lúc lỗi (để toast)
  const handleDialogLoadError = useCallback(
    (retry) => {
      retryLoadRef.current = typeof retry === 'function' ? retry : null;
      const wasOpen = isOpenRef.current;
      closeVoucherDetail();
      return wasOpen;
    },
    [closeVoucherDetail],
  );

  // giống VN: đổi trang (vd bấm vào sản phẩm trong popup) thì đóng popup
  useEffect(() => {
    router.events.on('routeChangeStart', closeVoucherDetail);
    return () => router.events.off('routeChangeStart', closeVoucherDetail);
  }, [router.events, closeVoucherDetail]);

  const actions = useMemo(
    () => ({ openVoucherDetail, closeVoucherDetail }),
    [openVoucherDetail, closeVoucherDetail],
  );

  const popupOptions = useMemo(() => ({ onCopy: popup.onCopy }), [popup.onCopy]);

  return (
    <VoucherDetailPopupActionsContext.Provider value={actions}>
      <VoucherDetailPopupStateContext.Provider value={popup.open}>
        {children}
        {hasOpened ? (
          <DialogLoadErrorContext.Provider value={handleDialogLoadError}>
            <VoucherDetailPopupOptionsContext.Provider value={popupOptions}>
              <VoucherDetailDialog
                open={popup.open}
                code={popup.code}
                initialVoucher={popup.initialVoucher}
                isMobile={isMobile}
                onClose={closeVoucherDetail}
              />
            </VoucherDetailPopupOptionsContext.Provider>
          </DialogLoadErrorContext.Provider>
        ) : null}
      </VoucherDetailPopupStateContext.Provider>
    </VoucherDetailPopupActionsContext.Provider>
  );
};

// Lồng nhau được: đã có provider cha (vd ở _app) thì chỉ render children và dùng popup của cha.
export const VoucherDetailPopupProvider = ({ children, isMobile = false }) => {
  const parentActions = useContext(VoucherDetailPopupActionsContext);
  if (parentActions) return <>{children}</>;
  return <VoucherDetailPopupRoot isMobile={!!isMobile}>{children}</VoucherDetailPopupRoot>;
};

const useActions = () => {
  const actions = useContext(VoucherDetailPopupActionsContext);
  if (!actions) {
    console.error('useVoucherDetailPopup must be used within VoucherDetailPopupProvider');
    return NOOP_ACTIONS;
  }
  return actions;
};

// Chỉ mở/đóng popup, không theo dõi isOpen (không re-render khi popup đóng/mở).
export const useVoucherDetailPopupActions = () => useActions();

// KH: tuỳ chọn của lần mở hiện tại ({ onCopy }), dùng trong nội dung popup
export const useVoucherDetailPopupOptions = () => useContext(VoucherDetailPopupOptionsContext);

// { openVoucherDetail(code, initialVoucher?, { onClose?, onCopy? }), closeVoucherDetail(), isOpen }
export const useVoucherDetailPopup = () => {
  const actions = useActions();
  const isOpen = useContext(VoucherDetailPopupStateContext);
  return useMemo(() => ({ ...actions, isOpen }), [actions, isOpen]);
};

export default VoucherDetailPopupProvider;
